/**
 * RunHealthStrip — the run-detail header's "last 10 runs + health" widget.
 *
 * Mirrors the flow-list "Recent runs" cell (same 10-segment pass/fail strip,
 * health icon, and pass-rate %), but each segment is a button that loads that
 * run, and the segment for the run you're viewing carries an active marker.
 *
 * @module ui/pages/RunDetailPage/RunHealthStrip
 */

import React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Tooltip, TooltipTrigger } from '@chatsdet/ui';
import type { WorkflowHealth } from '@chatsdet/types';
import type { WorkflowRunSummaryDto } from '../FlowDesigner/hooks/useWorkflowRuns';
import { workflowRunStatusTone } from '../flow-list/utils';
import { toneToResult, WORKFLOW_HEALTH_META } from '../flow-list/components/cells';
import { RunResultBars, type RunBarSegment } from '../../components/RunResultBars';

const STATUS_LABEL: Record<WorkflowRunSummaryDto['status'], string> = {
  queued: 'Queued',
  running: 'Running',
  success: 'Passed',
  failed: 'Failed',
  inconclusive: 'Platform error',
  canceled: 'Canceled',
  timeout: 'Timed out',
};

export const RunHealthStrip: React.FC<{
  runs: WorkflowRunSummaryDto[];
  activeRunId: string;
  health: WorkflowHealth | null;
}> = ({ runs, activeRunId, health }) => {
  const navigate = useNavigate();

  // Pass rate over terminal verdicts only (queued/running/canceled aren't a
  // verdict), matching FlowRecentRuns and the backend health classification.
  const passCount = runs.filter((r) => r.status === 'success').length;
  const failCount = runs.filter((r) => r.status === 'failed' || r.status === 'timeout').length;
  const total = passCount + failCount;
  const pct = total > 0 ? Math.round((passCount / total) * 100) : null;

  // Newest run on the right, older to the left (the strip pads the left to 10).
  const segments: RunBarSegment[] = runs
    .slice(0, 10)
    .reverse()
    .map((run) => {
      const isActive = run.id === activeRunId;
      return {
        id: run.id,
        result: toneToResult(workflowRunStatusTone[run.status]),
        isActive,
        ariaLabel: `Open ${STATUS_LABEL[run.status]} run${isActive ? ' (current)' : ''}`,
        tooltip: `${isActive ? 'Current run · ' : ''}${STATUS_LABEL[run.status]}`,
      };
    });

  const meta = health ? WORKFLOW_HEALTH_META[health] : null;

  return (
    <RunResultBars
      segments={segments}
      slots={10}
      onSelect={(runId) => navigate({ to: '/runs/$runId', params: { runId } })}
      trailing={
        <>
          {meta && (
            <TooltipTrigger delay={0} offset={8} placement="top">
              <span className="inline-flex cursor-default" aria-label={`Health: ${meta.label}`}>
                <meta.Icon size={15} weight="fill" style={{ color: meta.color }} />
              </span>
              <Tooltip variant="light" className="max-w-xs px-3 py-2 text-left">
                <div className="font-medium">{meta.label}</div>
                <div className="text-muted-foreground">{meta.description}</div>
              </Tooltip>
            </TooltipTrigger>
          )}

          {pct !== null && (
            <span
              className={`font-mono text-[11px] tabular-nums ${
                pct < 50 ? 'font-medium text-destructive' : 'text-muted-foreground'
              }`}
            >
              {pct}%
            </span>
          )}
        </>
      }
    />
  );
};
